import {
  Camera,
  Dumbbell,
  GraduationCap,
  Scissors,
  Sparkles,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";
import type { SeedTemplateKey } from "@/lib/service-seed";

/**
 * UI-side mirror of the backend `ServiceTemplates`. Backend owns the
 * canonical service-spec list; this only carries the icon, copy, and a
 * preview of what each template seeds (durations + relative prices).
 *
 * Add a template:
 *  1. Add to `ServiceTemplates.kt` on the backend.
 *  2. Add an entry below — keep `key` in sync with the backend map key.
 *  3. Frontend grid auto-renders the new card.
 */

export type TemplateOption = {
  key: SeedTemplateKey;
  label: string;
  description: string;
  icon: LucideIcon;
  preview: string[]; // bullet list of services it seeds
};

export const TEMPLATES: TemplateOption[] = [
  {
    key: "photography",
    label: "Photography studio",
    description: "Headshots, portraits, and event coverage.",
    icon: Camera,
    preview: [
      "1 service · 3 plans (Headshot / Photoshoot / Wedding)",
      "3 add-ons (Extra hour, Rush edit, Prints)",
      "Booking fields: event date, location, style",
    ],
  },
  {
    key: "salon",
    label: "Salon or barbershop",
    description: "Cuts, color, and treatments.",
    icon: Scissors,
    preview: [
      "1 service · 3 plans (Cut / Color / Treatment)",
      "3 add-ons (Conditioning, Scalp massage, Blow-dry)",
      "Booking fields: allergies, stylist, hair length",
    ],
  },
  {
    key: "coaching",
    label: "Coaching or consulting",
    description: "Intro calls and 1-on-1 sessions.",
    icon: GraduationCap,
    preview: [
      "1 service · 3 plans (Intro / Single / 5-pack)",
      "Booking fields: topic, goals, preferred medium",
    ],
  },
  {
    key: "fitness",
    label: "Fitness or personal training",
    description: "Sessions and prepaid bundles.",
    icon: Dumbbell,
    preview: [
      "1 service · 3 plans (Single / 5-pack / 10-pack)",
      "Booking fields: fitness level, health concerns, goals",
    ],
  },
  {
    key: "gown_rental",
    label: "Gown rental",
    description: "Formalwear rentals with fitting + pickup/return.",
    icon: Sparkles,
    preview: [
      "1 service · 4 sample rental gowns (replace with yours)",
      "4 add-ons (Veil, Shoes, Accessories, Delivery)",
      "Booking fields: event/pickup/return dates, size, color",
    ],
  },
  {
    key: "clinic",
    label: "Clinic / medical practice",
    description: "Consultations and follow-ups.",
    icon: Stethoscope,
    preview: [
      "1 service · 3 plans (Consult / Follow-up / Extended)",
      "Booking fields: reason, allergies, medications, insurance",
    ],
  },
];
